/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteOneAddIndex,
  setAddedIndexesInit,
  setAddedIndexesId,
  setAddedIndexesName,
  setAddedIndexesPeriodOfTime,
  setAddedIndexesSpatialStat,
  setAddedIndexesTempStat,
  setAddedIndexesTemporalMethod,
  setAddedIndexesYearFrom,
  setAddedIndexesYearTo,
  setAddedIndexesdayFrom,
  setAddedIndexesdayTo,
  setAddedIndexesmonthFrom,
  setAddedIndexesmonthTo,
  setSelectedYearFromHistoricalAdded,
  setSelectedYearToHistoricalAdded,
} from "../../reducers/PredictionSlice";
import {
  Autocomplete,
  TextField,
  Typography,
  Button,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Popover,
} from "@mui/material";
import "./style.css";
import CustomCheckBox from "../Monitoring/CustomCheckBox";
import { DateRangePicker } from 'react-date-range'; // Import react-date-range
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import YearPickerNormal from "../Monitoring/YearPickerNormal"
import { formatDateDisplay } from "../../utils/date";
import { getIndexYearAvailableFrom, getIndexYearAvailableTo } from "../../utils/available_dates";

const MainIndex = ({ indexes, showValidation = false }) => {
  const dispatch = useDispatch();

  const addIndexes = useSelector((state) => state.predictionState.addedIndexes);

  // --- State for Added Index Pickers --- 
  const [addedPickerAnchorEls, setAddedPickerAnchorEls] = useState({});

  const statsList = [
    { name: "Mean", key: "mean" },
    { name: "Min", key: "min" },
    { name: "Max", key: "max" },
    { name: "Median", key: "median" },
    { name: "Sum", key: "sum" },
    { name: "STD", key: "std" },
  ];

  const getDateRange = (index) => {
    if (!index) return [];
    try {
      const categories = JSON.parse(localStorage.getItem("categories")) || [];
      const currentIndex = categories.find((category) => category.name.toLowerCase() === index.toLowerCase());
      if (currentIndex && currentIndex.available_from && currentIndex.available_to) {
        return [new Date(currentIndex.available_from), new Date(currentIndex.available_to)];
      }
    } catch (error) {
      console.error("Error parsing categories from localStorage:", error);
    }
    return [];
  }

  // --- Helper function to format state for react-date-range ---
  const formatRangeForPicker = (yF, mF, dF, yT, mT, dT, indexName = null) => {
    const yearFrom = parseInt(yF, 10);
    const monthFrom = parseInt(mF, 10) - 1; // Month is 0-indexed
    const dayFrom = parseInt(dF, 10);
    const yearTo = parseInt(yT, 10);
    const monthTo = parseInt(mT, 10) - 1; // Month is 0-indexed
    const dayTo = parseInt(dT, 10);

    let startDate, endDate;

    // Check if we have valid selected dates
    const hasValidStartDate = !isNaN(yearFrom) && !isNaN(monthFrom) && !isNaN(dayFrom);
    const hasValidEndDate = !isNaN(yearTo) && !isNaN(monthTo) && !isNaN(dayTo);

    if (hasValidStartDate && hasValidEndDate) {
      // Use selected dates
      startDate = new Date(yearFrom, monthFrom, dayFrom);
      endDate = new Date(yearTo, monthTo, dayTo);
    } else {
      // No dates selected, use the available range for the index
      const availableRange = getDateRange(indexName);
      if (availableRange.length === 2) {
        startDate = availableRange[1]; // Use the last available date
        endDate = availableRange[1];   // Set both to last date initially
      } else {
        // Fallback to current date if no range available
        const currentDate = new Date();
        startDate = currentDate;
        endDate = currentDate;
      }
    }

    return [{
        startDate: startDate,
        endDate: endDate,
        key: 'selection'
    }];
  };

  const handleRangeChange = (item, index) => {
    const selectionKey = Object.keys(item)[0];
    const range = item[selectionKey];

    if (!range || !range.startDate || !range.endDate) {
      return;
    }

    const jsStartDate = new Date(range.startDate); // Use different var names
    const jsEndDate = new Date(range.endDate);

    const startYear = jsStartDate.getFullYear();
    const startMonth = jsStartDate.getMonth() + 1;
    const startDay = jsStartDate.getDate();
    const endYear = jsEndDate.getFullYear();
    const endMonth = jsEndDate.getMonth() + 1;
    const endDay = jsEndDate.getDate();

    dispatch(setAddedIndexesYearFrom({ index: index, yearFrom: startYear }));
    dispatch(setAddedIndexesmonthFrom({ index: index, monthFrom: startMonth }));
    dispatch(setAddedIndexesdayFrom({ index: index, dayFrom: startDay }));
    dispatch(setAddedIndexesYearTo({ index: index, yearTo: endYear }));
    dispatch(setAddedIndexesmonthTo({ index: index, monthTo: endMonth }));
    dispatch(setAddedIndexesdayTo({ index: index, dayTo: endDay }));
  };

  const formatDisplayDateRange = (yF, mF, dF, yT, mT, dT) => {
    const hasValidDates = !isNaN(parseInt(yF)) && !isNaN(parseInt(mF)) && !isNaN(parseInt(dF)) &&
                        !isNaN(parseInt(yT)) && !isNaN(parseInt(mT)) && !isNaN(parseInt(dT));

    if (!hasValidDates) {
      return "Select Date Range";
    }

    const range = formatRangeForPicker(yF, mF, dF, yT, mT, dT)[0];
    const startDate = range.startDate;
    const endDate = range.endDate;

    if (startDate && endDate) {
        const startStr = formatDateDisplay(startDate);
        const endStr = formatDateDisplay(endDate);
        return startDate.getTime() === endDate.getTime() ? startStr : `${startStr} - ${endStr}`;
    }
    return "Select Date Range";
  };

  const handleAddedPickerClick = (event, index) => {
    setAddedPickerAnchorEls(prev => ({
      ...prev,
      [index]: event.currentTarget
    }));
  };

  const handleAddedPickerClose = (index) => {
    setAddedPickerAnchorEls(prev => ({
      ...prev,
      [index]: null
    }));
  };

  // Get available date range for display
  const getAvailableDateRangeText = (indexName) => {
    const dateRange = getDateRange(indexName);
    if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      return `Available: ${formatDateDisplay(dateRange[0])} - ${formatDateDisplay(dateRange[1])}`;
    }
    return "";
  };

  const shouldShowErrorForAdded = (isValid) => {
    return showValidation && !isValid;
  };

  return (
    <div className="hide-scrollbar-tracker">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingRight: "250px",
          paddingLeft: "250px",
        }}
      >
        <Typography fontSize={24} fontWeight={600} fontFamily={"monospace"}>
          Indices
        </Typography>
        <Typography
          style={{ paddingTop: "10px", paddingBottom: "15px" }}
          fontSize={12}
          fontWeight={300}
          fontFamily={"monospace"}
        >
          Note: First Index is the Main Index
        </Typography>
        {addIndexes.map((added, addedIndex) => {
          const hasAddedDateRange = Boolean(
            typeof added.current_yearFrom === "number" &&
            typeof added.current_monthFrom === "number" &&
            typeof added.current_dayFrom === "number" &&
            typeof added.current_yearTo === "number" &&
            typeof added.current_monthTo === "number" &&
            typeof added.current_dayTo === "number",
          );
          const hasAddedSpatialStats = Boolean(added.spatialStats);
          const hasAddedTemporalStats = Boolean(added.temporalStats);
          const hasAddedHistYearFrom = Boolean(added.historical_yearFrom);
          const hasAddedHistYearTo = Boolean(added.historical_yearTo);
          const hasAddedIndex = Boolean(added.name && added.id);

          const indexYearAvailableFrom = getIndexYearAvailableFrom(added.id, 1900)
          const indexYearAvailableTo = getIndexYearAvailableTo(added.id, new Date().getFullYear())

          return (
            <div key={added.uid}>
              {addedIndex > 0 && (
                <Divider
                  sx={{
                    marginY: "30px",
                    "&::before, &::after": {
                      borderColor: "rgba(0, 0, 0, 0.2)",
                    },
                  }}
                />
              )}
              <Typography fontWeight={600}>Choose Index *</Typography>
              <br />
              <Autocomplete
                value={{ name: added.name }}
                size="small"
                disablePortal
                id="combo-box-demo"
                isOptionEqualToValue={(option, value) =>
                  option.name === value.name
                }
                getOptionLabel={(option) => (option ? option.name : "")}
                onChange={(_, selected) => {
                  dispatch(
                    setAddedIndexesName({
                      index: addedIndex,
                      name: selected.key,
                    }),
                  );
                  dispatch(
                    setAddedIndexesId({
                      index: addedIndex,
                      id: selected.id,
                    }),
                  );
                }}
                options={indexes || []}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Index"
                    required
                    error={shouldShowErrorForAdded(hasAddedIndex)}
                    helperText={
                      shouldShowErrorForAdded(hasAddedIndex)
                        ? "This field is required"
                        : ""
                    }
                  />
                )}
              />
              <br />
              <div>
                <Typography>Select Range *</Typography>
                {added.name && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", marginBottom: "8px" }}
                  >
                    {getAvailableDateRangeText(added.name)}
                  </Typography>
                )}
                {(() => {
                  const addedPickerAnchorEl = addedPickerAnchorEls[addedIndex];
                  const addedPickerOpen = Boolean(addedPickerAnchorEl);
                  const addedPickerId = addedPickerOpen
                    ? `added-daterange-popover-${addedIndex}`
                    : undefined;

                  return (
                    <>
                      <TextField
                        label="Selected Date Range"
                        variant="outlined"
                        fullWidth
                        size="small"
                        margin="normal"
                        required
                        error={shouldShowErrorForAdded(hasAddedDateRange)}
                        helperText={
                          shouldShowErrorForAdded(hasAddedDateRange)
                            ? "This field is required"
                            : ""
                        }
                        disabled={!added.name}
                        aria-describedby={addedPickerId}
                        value={formatDisplayDateRange(
                          added.current_yearFrom,
                          added.current_monthFrom,
                          added.current_dayFrom,
                          added.current_yearTo,
                          added.current_monthTo,
                          added.current_dayTo,
                        )}
                        onClick={(e) => handleAddedPickerClick(e, addedIndex)}
                        InputProps={{ readOnly: true }}
                      />
                      {!added.name && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", marginTop: "-8px" }}
                        >
                          Please select an index first to enable date selection.
                        </Typography>
                      )}
                      <Popover
                        id={addedPickerId}
                        open={addedPickerOpen}
                        anchorEl={addedPickerAnchorEl}
                        onClose={() => handleAddedPickerClose(addedIndex)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                      >
                        <DateRangePicker
                          staticRanges={[]}
                          ranges={formatRangeForPicker(
                            added.current_yearFrom,
                            added.current_monthFrom,
                            added.current_dayFrom,
                            added.current_yearTo,
                            added.current_monthTo,
                            added.current_dayTo,
                            added.name,
                          )}
                          onChange={(item) =>
                            handleRangeChange(item, addedIndex)
                          }
                          minDate={getDateRange(added.name)[0]}
                          maxDate={getDateRange(added.name)[1]}
                          showSelectionPreview={true}
                          moveRangeOnFirstSelection={false}
                          months={1}
                          direction="horizontal"
                          showDateDisplay={false}
                          editableDateInputs={false}
                        />
                      </Popover>
                    </>
                  );
                })()}
              </div>
              <br />
              <div>
                <InputLabel
                  id={"added-spatial-stats-label" + addedIndex}
                  required
                >
                  Spatial Stats
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  required
                  error={shouldShowErrorForAdded(hasAddedSpatialStats)}
                  labelId={"added-spatial-stats-label" + addedIndex}
                  id={"added-spatial-stats-select" + addedIndex}
                  value={added.spatialStats}
                  onChange={(event) => {
                    dispatch(
                      setAddedIndexesSpatialStat({
                        index: addedIndex,
                        stat: event.target.value,
                      }),
                    );
                  }}
                >
                  {statsList.map((stat, statIndex) => {
                    return (
                      <MenuItem key={statIndex} value={stat.key}>
                        {stat.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {shouldShowErrorForAdded(hasAddedSpatialStats) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", marginTop: "4px" }}
                  >
                    This field is required
                  </Typography>
                )}
              </div>
              <br />
              <div>
                <InputLabel
                  id={"added-temporal-stats-label" + addedIndex}
                  required
                >
                  Temporal Stats
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  required
                  error={shouldShowErrorForAdded(hasAddedTemporalStats)}
                  labelId={"added-temporal-stats-label" + addedIndex}
                  id={"added-temporal-stats-select" + addedIndex}
                  value={added.temporalStats}
                  onChange={(event) => {
                    dispatch(
                      setAddedIndexesTempStat({
                        index: addedIndex,
                        stat: event.target.value,
                      }),
                    );
                  }}
                >
                  {statsList.map((stat, statIndex) => {
                    return (
                      <MenuItem key={statIndex} value={stat.key}>
                        {stat.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {shouldShowErrorForAdded(hasAddedTemporalStats) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", marginTop: "4px" }}
                  >
                    This field is required
                  </Typography>
                )}
              </div>
              <br />
              <div>
                <CustomCheckBox
                  title={"Time-step"}
                  data={added.periodOfTime}
                  dispatch={dispatch}
                  reduxFunc={setAddedIndexesPeriodOfTime}
                  index={addedIndex}
                />
              </div>
              <br />
              <CustomCheckBox
                title={"Temporal Method"}
                data={added.temporalMethod}
                dispatch={dispatch}
                reduxFunc={setAddedIndexesTemporalMethod}
                index={addedIndex}
              />

              <div style={{ width: "100%", marginTop: "20px" }}>
                <Typography
                  fontSize={16}
                  fontWeight={600}
                  fontFamily={"monospace"}
                  textAlign={"left"}
                >
                  Historical Data Range *
                </Typography>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "50px",
                    marginTop: "10px",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <Typography sx={{ marginTop: "10px" }}>From *</Typography>
                    <YearPickerNormal
                      style={{ width: "100%" }}
                      setYear={(value) => dispatch(setSelectedYearFromHistoricalAdded({ index: addedIndex, year: value }))}
                      yearFrom={indexYearAvailableFrom}
                      yearTo={added.historical_yearTo || indexYearAvailableTo}
                      label="Year From"
                      defValue={added.historical_yearFrom}
                    />
                    {shouldShowErrorForAdded(hasAddedHistYearFrom) && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", marginTop: "4px" }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </div>
                  <div style={{ width: "100%" }}>
                    <Typography sx={{ marginTop: "10px" }}>To *</Typography>
                    <YearPickerNormal
                      style={{ width: "100%" }}
                      setYear={(value) => dispatch(setSelectedYearToHistoricalAdded({ index: addedIndex, year: value }))}
                      yearFrom={added.historical_yearFrom || indexYearAvailableFrom}
                      yearTo={indexYearAvailableTo}
                      label="Select Year"
                      defValue={added.historical_yearTo}
                    />
                    {shouldShowErrorForAdded(hasAddedHistYearTo) && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", marginTop: "4px" }}
                      >
                        This field is required
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
              <br />
              <br />

              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  dispatch(deleteOneAddIndex({ index: addedIndex }));
                }}
              >
                Delete this index
              </Button>
              <br />
              <br />
            </div>
          );
        })}
        <Button
          onClick={() => {
            dispatch(setAddedIndexesInit());
          }}
          style={{ fontSize: "20px" }}
        >
          Add index
        </Button>
      </div>
    </div>
  );
};

export default MainIndex;
